import { X, Clock, User, Image as ImageIcon, MapPin, Timer } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import RouteMap from "@/components/RouteMap";

const STAGE_LABELS = {
  factura: "Factura",
  embarque: "Embarque",
  en_ruta: "En Ruta",
  entregado: "Entregado",
};

function fmt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function HistoryModal({ delivery, onClose }) {
  const [imgUrl, setImgUrl] = useState(null);

  useEffect(() => {
    if (!delivery?.evidencia_path) return;
    let url = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/files/${delivery.evidencia_path}`, { responseType: "blob" });
        url = URL.createObjectURL(res.data);
        if (!cancelled) setImgUrl(url);
      } catch (error) {
        console.warn("No se pudo cargar la imagen de evidencia", error);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [delivery]);

  if (!delivery) return null;
  const history = delivery.history || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-zinc-950/60 backdrop-blur-sm p-4"
      data-testid="history-modal"
    >
      <div className="bg-white border border-zinc-200 w-full max-w-xl my-4 sm:my-8 flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)]">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200 flex-shrink-0 bg-white">
          <div className="min-w-0 flex-1 pr-3">
            <p className="eyebrow text-zinc-500">Historial de entrega</p>
            <h2 className="font-display text-xl font-bold mt-1 truncate">{delivery.cliente}</h2>
            <p className="text-sm text-zinc-500 mt-0.5 truncate">{delivery.herramienta}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="btn-close-history"
            aria-label="Cerrar historial"
            className="p-2 hover:bg-zinc-100 flex-shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 min-h-0">
          {delivery.recibido_por && (
            <div className="mb-5 border border-emerald-200 bg-emerald-50 p-3">
              <p className="eyebrow text-emerald-700 mb-1">Recibido por</p>
              <p className="font-display text-lg font-bold text-emerald-900">{delivery.recibido_por}</p>
              {delivery.entregado_at && (
                <p className="text-xs text-emerald-700 mt-0.5">{fmt(delivery.entregado_at)}</p>
              )}
            </div>
          )}

          {delivery.evidencia_path && (
            <div className="mb-5">
              <p className="eyebrow text-zinc-500 mb-2 flex items-center gap-1.5">
                <ImageIcon className="h-3 w-3" /> Evidencia
              </p>
              {imgUrl ? (
                <img src={imgUrl} alt="Evidencia de entrega" className="w-full max-h-80 object-contain border border-zinc-200 bg-zinc-50" data-testid="history-evidencia-img" />
              ) : (
                <div className="w-full h-32 border border-zinc-200 bg-zinc-50 animate-pulse" />
              )}
            </div>
          )}

          {(delivery.route_started_location || delivery.destination_location) && (
            <div className="mb-5">
              <p className="eyebrow text-zinc-500 mb-2 flex items-center gap-1.5">
                <MapPin className="h-3 w-3" /> Recorrido GPS
              </p>
              <RouteMap origin={delivery.route_started_location} destination={delivery.destination_location} />
              {delivery.route_started_at && delivery.entregado_at && (
                <div className="mt-2 inline-flex items-center gap-2 text-xs text-ekin-700 border border-ekin-100 bg-ekin-50 px-3 py-1.5" data-testid="route-duration">
                  <Timer className="h-3.5 w-3.5" />
                  <span>
                    Tiempo de ruta:{" "}
                    <strong>
                      {(() => {
                        const ms = new Date(delivery.entregado_at) - new Date(delivery.route_started_at);
                        if (ms < 0) return "—";
                        const m = Math.round(ms / 60000);
                        return m < 60 ? `${m} min` : `${(m / 60).toFixed(1)} h`;
                      })()}
                    </strong>
                  </span>
                </div>
              )}
            </div>
          )}

          <p className="eyebrow text-zinc-500 mb-4">Línea de tiempo</p>
          <ol className="relative border-l border-zinc-200 ml-2 space-y-5">
            {history.length === 0 && (
              <li className="text-sm text-zinc-500 ml-4">Sin historial</li>
            )}
            {history.map((h) => (
              <li key={`${h.stage}-${h.at}`} className="ml-4" data-testid={`history-item-${h.stage}-${h.at}`}>
                <div className={`absolute -left-1.5 h-3 w-3 bg-white border-2 stage-${h.stage}`} style={{ borderColor: "currentColor" }} />
                <div className="flex items-center gap-2">
                  <span className={`eyebrow stage-${h.stage}`}>{STAGE_LABELS[h.stage] || h.stage}</span>
                  <span className="text-xs text-zinc-400">·</span>
                  <span className="text-xs text-zinc-500 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {fmt(h.at)}
                  </span>
                </div>
                <p className="text-sm mt-1 flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5 text-zinc-400" />
                  {h.by}
                  {h.recibido_por && (
                    <span className="text-zinc-500">— recibido por <strong>{h.recibido_por}</strong></span>
                  )}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
